import { useDisclosure } from "@chakra-ui/react"
import { useEffect, useState } from "react"
import type { UseFormReturn } from "react-hook-form"
import { type Location, useBlocker, useNavigate } from 'react-router-dom'
import type { Product } from "../enums"
import { getObjectKeyValues } from '../Utils/objectKeyUtils'

export const useClearInsuranceParamsFormPersistance = () => {
    return () => {
        for (const key in window.localStorage)
            if (key.startsWith(`insuranceParamsForm_`))
                clearLocalStorage(key)
    }
}

export const useInsuranceParamsFormPersistance = (
    product: Product | string,
    step: number,
    insuranceParamsId: string | undefined,
    { getValues, setValue, formState: { dirtyFields, isSubmitSuccessful } }: UseFormReturn<any, any, any>
) => {
    const localStorageKey = `insuranceParamsForm_${product}_${insuranceParamsId ? insuranceParamsId : 'new'}_${step}` as const

    const { isOpen: isOpenUnsavedModal, onOpen: onOpenUnsavedModal, onClose: onCloseUnsavedModal } = useDisclosure()

    // isDirty z RHF funguje nejak zvlastne a obcas i kdyz je dirtyField prazdne, tak ukazuje, ze je formular zmenen
    const isDirty = Object.keys(dirtyFields).length > 0

    const [shouldBlockNavigation, setShouldBlockNavigation] = useState(false) // zda se muze prerusit navigace
    const [nextLocation, setNextLocation] = useState<Location | null>(null) // prerusena cesta navigovani
    const navigate = useNavigate() // pro pokracovani v navigovani

    // Blokování navigace
    useBlocker(
        (transition) => {
            if (!shouldBlockNavigation)
                return false

            if (!isDirty)
                return false

            // if only dirty field is contractReplacementNumber, then allow navigation
            if (Object.keys(dirtyFields).length === 1 && dirtyFields?.generalParams?.contractReplacementNumber)
                return false

            setNextLocation(transition.nextLocation) // uložení si cesty, pokud uz. bude chtit pokracovat v navigaci
            onOpenUnsavedModal()
            return true
        }
    )

    // vynuceni navigace, pokud uz. nechce zustat na formulari
    useEffect(() => {
        if (!shouldBlockNavigation && nextLocation)
            navigate(nextLocation.pathname)
    }, [shouldBlockNavigation, nextLocation])

    // handler pro pokracovani v navigaci
    const proceedNavigation = () => {
        setShouldBlockNavigation(false)
        onCloseUnsavedModal()
    }

    // handler pro preruseni navigace
    const cancelNavigation = () => {
        setShouldBlockNavigation(true)
        setNextLocation(null)
        onCloseUnsavedModal()
    }

    // nacteni ulozenych dat v local storage pri page load
    useEffect(() => {
        if (getFromLocalStorage(localStorageKey)) {
            onOpenUnsavedModal()
        }
    }, [])

    // handler pri opusteni okna (reload apod.). 
    // Pokud uzivatel ma zmeneny formular, tak se hodnoty ulozi do local storage.
    // Pokud zmenen neni, tak se local storage naopak promaze.
    const handleWindowUnload = () => {
        if (isDirty)
            storeToLocalStorage(localStorageKey, getValues())
        else
            clearLocalStorage(localStorageKey)
    }

    const eventName = `unload`;
    useEffect(() => {
        // nastaveni priznaku pro preruseni navigace, pokud uzivatel zmeni hodnoty ve formulari
        setShouldBlockNavigation(isDirty && !isSubmitSuccessful)

        // nabindovani handleru na udalost opusteni okna
        window.addEventListener(eventName, handleWindowUnload)
        return () => {
            window.removeEventListener(eventName, handleWindowUnload)
        }
    }, [isDirty, isSubmitSuccessful])

    // handler pro pokracovani bez ulozenych zmen
    const handleNoClick = () => {
        // rozdilne chovani, pokud se jedna o navigaci jinam, nebo zavreni okna
        if (nextLocation != null)
            return proceedNavigation()

        onCloseUnsavedModal()
        clearLocalStorage(localStorageKey)
    }

    // handler pro pokračování v úpravách
    const handleYesClick = () => {
        // rozdilne chovani, pokud se jedna o navigaci jinam, nebo zavreni okna
        if (nextLocation != null)
            return cancelNavigation()

        const formValues = getFromLocalStorage(localStorageKey)!

        // rekurzivne ziska cestu ke kazdemu fieldu a po jednom je prenastavi (nelze prenastavit cely objekt)
        for (const x of getObjectKeyValues(formValues)) {
            setValue(x.fieldName as any, x.value, { shouldDirty: true })
        }

        onCloseUnsavedModal()
    }

    // handler pro promazání local storage při odeslání formuláře
    const clear = () => {
        if (insuranceParamsId)
            for (const key in window.localStorage)
                if (key.startsWith(`insuranceParamsForm_${product}_${insuranceParamsId}`))
                    clearLocalStorage(key)

        clearLocalStorage(localStorageKey)
    }

    return {
        isOpenUnsavedModal,
        onCloseUnsavedModal,
        handleNoClick,
        handleYesClick,
        clear
    }
}

export const storeToLocalStorage = (key: string, item: object) => {
    window.localStorage.setItem(key, JSON.stringify(item))
}

export const getFromLocalStorage = <T extends object>(key: string): T | null => {
    const item = window.localStorage.getItem(key)
    return item ? JSON.parse(item) as T : null
}

export const clearLocalStorage = (key?: string) => {
    if (key)
        window.localStorage.removeItem(key)
    else
        window.localStorage.clear()
}
